import React, { useEffect } from "react"

import styled from "styled-components"

import { useLocalization } from "@izettle/lingo-client"
import { colors, spacing } from "@izettle/otto"
import { Text, Button, MessageType } from "@izettle/react"

import { Cancel } from "@/components/cancel"
import { PageWrapper } from "@/components/layout-helpers"
import { Message } from "@/components/message"
import { useSetPageTitle } from "@/components/page-title"
import { theme } from "@/theme"

const PageTitle = styled(Text.Title2)`
  margin: ${spacing.small} ${spacing.none};
`

const StyledMessage = styled(Message)`
  margin-bottom: ${spacing.large};
  background-color: ${colors.message.background.info};
  color : ${colors.text.default};
  border: none;
`

const StepsList = styled.ol`
  margin: ${spacing.none} ${spacing.none} ${spacing.xLarge};
  padding: ${spacing.none};
`

const Step = styled.li`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: ${spacing.small};

  &:last-child {
    margin-bottom: ${spacing.none};
  }
`

const StepNumber = styled(Text.Title1)`
  margin: ${spacing.none};
  color: ${colors.text.default};

  @media ${theme.devices.tinyMobile} {
    padding: ${spacing.none} ${spacing.small};
  }

  @media ${theme.devices.mobile} {
    padding: ${spacing.none} ${spacing.xxSmall};
  }
`

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
`

const StepTitle = styled(Text.RegularBold)`
  margin: ${spacing.none};
`

const StepText = styled(Text.RegularSecondary)`
  margin: ${spacing.none};
`

const steps: number[] = [1, 2, 3]

type OverviewProps = {
  backUrl: string
  nextUrl: string
  redirectNext: (redirectUrl: string) => void
}

export const Overview: React.FC<OverviewProps> = ({ backUrl, nextUrl, redirectNext }) => {
  const { text } = useLocalization()
  const { setPageTitle } = useSetPageTitle()

  useEffect(() => {
    setPageTitle(text("PROOF_OF_AUTHORIZATION"))
  }, [])

  return (
    <PageWrapper>
      <Cancel url={backUrl} />
      <PageTitle>{text("LOA_OVERVIEW_TITLE")}</PageTitle>
      <StyledMessage
        showIcon
        title={text("LOA_OVERVIEW_INFO_TITLE")}
        type={MessageType.INFO}
      >
        {text("LOA_OVERVIEW_INFO_TEXT")}
      </StyledMessage>

      <StepsList role="list">
        {steps.map((step) => (
          <Step role="listitem" key={step}>
            <StepNumber aria-hidden>{step}</StepNumber>
            <StepContent>
              <StepTitle>{text(`LOA_OVERVIEW_STEP${step}_TITLE`)}</StepTitle>
              <StepText>{text(`LOA_OVERVIEW_STEP${step}_TEXT`)}</StepText>
            </StepContent>
          </Step>
        ))}
      </StepsList>
      <Button onClick={() => redirectNext(nextUrl)}>{text("CONTINUE")}</Button>
    </PageWrapper>
  )
}
